import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Space, notification, Modal, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import * as actions from '../store/actions';

import { PdfViewerComponent } from '../../../shared/components/pdf-viewer';
import { isEmpty, isString } from 'lodash';

const fillReverseMapping = ({ rootContainer, formId, redirectUrl, userId, agencyId }) => {
  const dispatch = useDispatch();

  const [formSaveCallback, setFormSaveCallback] = useState();
  const [formClearCallback, setFormClearCallback] = useState();
  const [localFileBytes, setLocalFileBytes] = useState();

  const submitFormLoading = useSelector((state) => state.reverseMapping.submitFormLoading);
  const reverseMappingForm = useSelector((state) => state.reverseMapping.reverseMappingForm);
  const reverseMappingFormLoading = useSelector((state) => state.reverseMapping.reverseMappingFormLoading);

  useEffect(() => {
    dispatch(actions.getReverseMappingForm({ formId, userId, agencyId }));
  }, []);

  useEffect(() => {
    if (reverseMappingForm) {
      setLocalFileBytes(reverseMappingForm);
    }
  }, [reverseMappingForm]);

  const onClearForm = () => {
    formClearCallback();
  };

  const onSubmitForm = () => {
    if (typeof formSaveCallback === 'function') {
      formSaveCallback().then((formDataArray) => {
        dispatch(actions.submitForm({ formId, formDataArray: Array.from(new Uint8Array(formDataArray)), userId, agencyId })).then((result) => {
          notify('Your form has been sent successfully.');

          let messageText = 'You can view the filled pdf form under the tab Pdf Forms - Reverse Mapping - Filled.';
          if (result && result.isIntegrationPage === true) {
            messageText = 'Thank you! Your request has been successfully submitted and an agent will be in touch shortly with rate options.';
          }

          Modal.success({
            content: messageText,
            getContainer: rootContainer,
            onOk: () => {
              if (redirectUrl) {
                window.location.href = redirectUrl;
              } else {
                formClearCallback();
              }
            },
          });
        });
      });
    }
  };

  const notify = (description) => {
    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      getContainer: rootContainer,
      description: description,
    });
  };

  const handleFileUpload = (file) => {
    const isPdf = file.type === 'application/pdf';
    const isLt8M = file.size / 1024 / 1024 < 8;

    if (!isPdf) {
      notification.error({
        message: 'Error',
        description: 'You can only upload PDF files!',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
      });
      return false;
    }

    if (!isLt8M) {
      notification.error({
        message: 'Error',
        description: 'File must be smaller than 8MB!',
        top: 55,
        duration: 10,
        getContainer: rootContainer,
      });
      return false;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      // Add loading
      notification.info({
        message: 'Loading',
        description: 'Uploading and processing the PDF file...',
        top: 55,
        duration: 0,
        getContainer: rootContainer,
        key: 'uploadNotification',
      });

      // Add check for file match with form fields
      const pdfjsLib = await import('pdfjs-dist');
      const pdf = await pdfjsLib.getDocument({ data: e.target.result }).promise;
      const numPages = pdf.numPages;
      const formFields = [];

      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const annotations = await page.getAnnotations();

        annotations.forEach((annotation) => {
          if (isString(annotation.fieldName) && !isEmpty(annotation.fieldName)) {
            formFields.push(annotation.fieldName);
          }
        });
      }

      const pdfjsLibOld = await import('pdfjs-dist');
      const oldPdf = await pdfjsLibOld.getDocument({ data: reverseMappingForm }).promise;
      const oldNumPages = oldPdf.numPages;
      const oldFormFields = [];

      for (let i = 1; i <= oldNumPages; i++) {
        const page = await oldPdf.getPage(i);
        const annotations = await page.getAnnotations();

        annotations.forEach((annotation) => {
          if (isString(annotation.fieldName) && !isEmpty(annotation.fieldName)) {
            oldFormFields.push(annotation.fieldName);
          }
        });
      }

      const missingFields = oldFormFields.filter((field) => !formFields.includes(field));

      if (missingFields.length > 0) {
        notification.error({
          message: 'Error',
          description: `The uploaded PDF is missing some fields that are existing in the original PDF.`,
          top: 55,
          duration: 10,
          getContainer: rootContainer,
          key: 'uploadNotification',
        });
        return;
      }

      notification.close('uploadNotification');
      setLocalFileBytes(new Uint8Array(e.target.result));
    };

    reader.readAsArrayBuffer(file);

    return false; // Prevent upload
  };

  return (
    <div id="fillReverseMapping">
      <PdfViewerComponent
        fileBytes={localFileBytes}
        registerFormSaveCallback={(callback) => setFormSaveCallback(() => callback)}
        rootContainer={rootContainer}
        loading={!localFileBytes || reverseMappingFormLoading || submitFormLoading}
        registerFormClearCallback={(callback) => setFormClearCallback(() => callback)}
        disableDeletePages={true}
      />
      <div className="action-buttons">
        <Space>
          <Upload beforeUpload={handleFileUpload} showUploadList={false} disabled={submitFormLoading || reverseMappingFormLoading}>
            <Button icon={<UploadOutlined />} loading={submitFormLoading || reverseMappingFormLoading}>
              Upload filled PDF
            </Button>
          </Upload>
          <Button onClick={onClearForm} loading={submitFormLoading || reverseMappingFormLoading}>
            Clear Form
          </Button>
          <Button type="primary" loading={submitFormLoading || reverseMappingFormLoading} onClick={onSubmitForm}>
            Submit
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default fillReverseMapping;
